/**
 * PortPairSearchPanel
 * ─────────────────────────────────────────────────────────────
 * One-shot search for staff to view EVERYTHING about a (POL, POD)
 * pair: Freightlinks buy rate, Europacific sell rate, competitor
 * rates, origin/destination charges, port surcharges and matching
 * agents — without leaving the page.
 *
 * Lives as a sub-tab in the Pricing Module page AND as an inline
 * panel inside the "Create Offer from Scratch" dialog.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Search, Ship, Anchor, AlertTriangle, CheckCircle2, ChevronRight,
  Users, MapPin, Mail, Phone, Loader2, X, ExternalLink, RefreshCw,
} from "lucide-react";

const fmt = (n, ccy = "USD") =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `${ccy === "USD" ? "$" : ""}${Number(n).toFixed(2)}${ccy !== "USD" ? " " + ccy : ""}`;

function ChargesTable({ title, rows, accent = "slate" }) {
  if (!rows?.length) {
    return (
      <div className={`rounded-lg border border-${accent}-200 bg-${accent}-50/40 p-4`}>
        <div className="font-semibold text-slate-800 mb-1">{title}</div>
        <div className="text-xs text-slate-500">No data yet (run the Freightlinks charges scraper or seed via Settings).</div>
      </div>
    );
  }
  return (
    <div className={`rounded-lg border border-${accent}-200 bg-white overflow-hidden`}>
      <div className={`px-4 py-2 bg-${accent}-50 font-semibold text-slate-800 text-sm border-b border-${accent}-200`}>{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs">
          <tr>
            <th className="px-3 py-2 text-left">Code</th>
            <th className="px-3 py-2 text-left">Description</th>
            <th className="px-3 py-2 text-left">Basis</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono text-xs text-slate-600">{c.code || "—"}</td>
              <td className="px-3 py-2">{c.label || c.description || "—"}</td>
              <td className="px-3 py-2 text-slate-500 text-xs">{(c.basis || "per_shipment").replace("_", " ")}</td>
              <td className="px-3 py-2 text-right font-medium">{fmt(c.amount, c.currency || "USD")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AgentCard({ a }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
      <div className="font-semibold text-slate-900">{a.company_name}</div>
      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
        <MapPin className="h-3 w-3" /> {[a.city, a.country].filter(Boolean).join(", ") || "—"}
      </div>
      {a.name && <div className="text-xs text-slate-600 mt-1">{a.name}</div>}
      {a.email && (
        <a href={`mailto:${a.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
          <Mail className="h-3 w-3" /> {a.email}
        </a>
      )}
      {a.phone && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {a.phone}</div>}
      {a.website && (
        <a href={a.website} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:underline flex items-center gap-1 mt-0.5">
          <ExternalLink className="h-3 w-3" /> {a.website}
        </a>
      )}
    </div>
  );
}

export default function PortPairSearchPanel({ defaultPol = "", defaultPod = "SIKOP", compact = false }) {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [polQuery, setPolQuery] = useState(defaultPol);
  const [pod, setPod] = useState(defaultPod);
  const [ports, setPorts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  // 2026-05-27 — direction toggle. Was previously hardcoded to "import",
  // which made it impossible to check export rates.
  const [direction, setDirection] = useState("import");
  // Export pricing: final sell = FL Singapore↔dest buy rate + Leg-1
  // addon (Koper↔Singapore transhipment) + profit margin. Both are
  // editable per search so sales can flex on close. Defaults agreed
  // 2026-05-27 with Rok: $35/WM transhipment, $25/WM margin.
  const [leg1Addon, setLeg1Addon] = useState(35);
  const [exportMargin, setExportMargin] = useState(25);

  // Fetch port list whenever direction changes (autocomplete pool comes
  // from the latest rate card of that direction).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/port-pair/ports`, {
          headers: auth,
          params: { direction },
        });
        if (!cancelled) setPorts(res.data?.items || []);
      } catch (e) {
        // silently ignore — autocomplete is best-effort
      }
    })();
    return () => { cancelled = true; };
  }, [auth, direction]);

  // Update suggestions on typing
  useEffect(() => {
    const q = polQuery.trim().toLowerCase();
    if (!q) { setSuggestions([]); return; }
    const matches = ports.filter(p =>
      (p.pol_name || "").toLowerCase().includes(q) ||
      (p.pol || "").toLowerCase().includes(q)
    ).slice(0, 8);
    setSuggestions(matches);
  }, [polQuery, ports]);

  const runSearch = useCallback(async (polValue) => {
    const pol = (polValue ?? polQuery).trim();
    if (!pol) {
      toast.error("Enter a port name or UN/LOCODE first");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API_URL}/port-pair/search`, {
        headers: auth,
        params: { pol, pod, direction },
      });
      setResult(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Search failed";
      toast.error(typeof msg === "string" ? msg : "Search failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [polQuery, pod, auth]);

  const clear = () => {
    setPolQuery("");
    setPod(defaultPod);
    setResult(null);
    setSearched(false);
  };

  // Re-run search whenever direction flips, IF a previous search was made,
  // so the user immediately sees the export numbers without re-clicking.
  useEffect(() => {
    if (searched && polQuery.trim()) {
      runSearch(polQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  const pickSuggestion = (p) => {
    setPolQuery(p.pol_name);
    setShowSugg(false);
    runSearch(p.pol_name);
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className={`bg-white rounded-xl border border-slate-200 ${compact ? "p-4" : "p-6"}`} data-testid="port-pair-search-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className={`${compact ? "text-base" : "text-lg"} font-bold text-slate-900 flex items-center gap-2`}>
            <Anchor className="h-5 w-5 text-blue-600" />
            Port-Pair Search
          </h2>
          {!compact && (
            <p className="text-sm text-slate-500 mt-0.5">
              All-in-one view: rate, charges, agents and surcharges for any (POL → POD) pair.
            </p>
          )}
        </div>
        {!compact && (
          <Button variant="outline" size="sm" onClick={clear} data-testid="port-pair-clear">
            <RefreshCw className="h-4 w-4 mr-1" /> Reset
          </Button>
        )}
      </div>

      {/* Direction toggle — IMPORT (to Koper) ↔ EXPORT (from Koper).
          Was previously hardcoded so export rates were unreachable. */}
      <div className="inline-flex items-center gap-0 rounded-lg border border-slate-200 overflow-hidden mb-4 text-xs" data-testid="port-pair-direction-toggle">
        <button
          type="button"
          onClick={() => setDirection("import")}
          className={`px-3 py-1.5 font-medium ${direction === "import" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="port-pair-direction-import"
        >
          ⬇ IMPORT (to Koper)
        </button>
        <button
          type="button"
          onClick={() => setDirection("export")}
          className={`px-3 py-1.5 font-medium border-l border-slate-200 ${direction === "export" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid="port-pair-direction-export"
        >
          ⬆ EXPORT (from Koper)
        </button>
      </div>

      {/* Search inputs */}
      <div className="grid grid-cols-12 gap-3 mb-5">
        <div className="col-span-12 md:col-span-7 relative">
          <label className="text-xs text-slate-500 font-medium">
            {direction === "import" ? "Origin (POL)" : "Destination (POD)"}
          </label>
          <div className="relative">
            <Input
              value={polQuery}
              onChange={(e) => { setPolQuery(e.target.value); setShowSugg(true); }}
              onFocus={() => setShowSugg(true)}
              onBlur={() => setTimeout(() => setShowSugg(false), 150)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder={direction === "import" ? "Port or country — e.g. SHANGHAI, NHAVA SHEVA, India, CN…" : "Port or country — e.g. NEW YORK, MOMBASA, USA, KE…"}
              className="pl-9"
              data-testid="port-pair-pol-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            {polQuery && (
              <button onClick={() => setPolQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {showSugg && suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="port-pair-suggestions">
              {suggestions.map((p, i) => (
                <button key={i}
                  onMouseDown={(e) => { e.preventDefault(); pickSuggestion(p); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center justify-between text-sm">
                  <span><span className="font-medium">{p.pol_name}</span> <span className="text-slate-400 text-xs">({p.pol})</span></span>
                  {p.min_rate ? <span className="text-emerald-600 text-xs">from ${p.min_rate}/CBM</span> : null}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="col-span-8 md:col-span-3">
          <label className="text-xs text-slate-500 font-medium">
            {direction === "import" ? "Destination (POD)" : "Origin (POL)"}
          </label>
          <Input
            value={pod}
            onChange={(e) => setPod(e.target.value.toUpperCase())}
            placeholder="SIKOP"
            data-testid="port-pair-pod-input"
            title={direction === "export"
              ? "For EXPORT this is the origin port (we ship from Koper unless overridden)."
              : "For IMPORT this is the destination port (typically SIKOP / Koper)."}
          />
        </div>
        <div className="col-span-4 md:col-span-2 flex items-end">
          <Button
            onClick={() => runSearch()}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="port-pair-search-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Search</>}
          </Button>
        </div>
      </div>

      {/* Result */}
      {!searched && (
        <div className="text-center py-10 text-slate-400 text-sm">
          <Ship className="h-10 w-10 mx-auto mb-2 opacity-40" />
          Start by typing an origin port above.
        </div>
      )}

      {searched && result && !result.found && (
        <div className="space-y-3" data-testid="port-pair-not-found">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">
                {result.mode === "country"
                  ? `${result.country}: ${result.alternatives?.length || 0} priced port${(result.alternatives?.length || 0) === 1 ? "" : "s"}`
                  : result.alternatives?.length
                    ? `No exact match — showing alternatives in ${result.suggested_country}`
                    : "No match in Freightlinks card"}
              </div>
              <div className="text-amber-700">
                {result.message || "Try a different port name or upload a fresh Freightlinks card."}
              </div>
            </div>
          </div>

          {!!result.alternatives?.length && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="port-pair-alternatives">
              <div className="px-4 py-2 bg-slate-50 font-semibold text-slate-800 text-sm border-b border-slate-200 flex items-center gap-2">
                <Anchor className="h-4 w-4 text-blue-600" />
                {result.mode === "country" ? `Ports in ${result.country}` : `Alternative ports`}
                <span className="text-xs text-slate-400 font-normal">— sorted by best buy rate</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="px-3 py-2 text-left">Port</th>
                    <th className="px-3 py-2 text-left">UN/LOCODE</th>
                    <th className="px-3 py-2 text-right">Buy rate / CBM</th>
                    <th className="px-3 py-2 text-right">Transit (to hub)</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {result.alternatives.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-blue-50/40">
                      <td className="px-3 py-2 font-medium text-slate-900">{p.pol_name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{p.pol}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                        {p.min_rate_cbm != null ? `$${Number(p.min_rate_cbm).toFixed(2)}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600">
                        {p.transit_days != null ? `${p.transit_days} d` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setPolQuery(p.pol_name); runSearch(p.pol_name); }}
                          data-testid={`port-pair-alt-pick-${i}`}
                        >
                          View details <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {searched && result?.found && (
        <div className="space-y-5" data-testid="port-pair-result">
          {/* Header bar */}
          <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-white p-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 font-semibold text-slate-900">
              <Ship className="h-5 w-5 text-blue-600" />
              {result.pol.name}
              <span className="text-slate-400 text-xs">({result.pol.code})</span>
              <span className="text-slate-500">·</span>
              <span className="text-xs text-slate-500">{result.pol.country || "Unknown"}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <div className="flex items-center gap-2 font-semibold text-slate-900">
              {result.pod.name} <span className="text-slate-400 text-xs">({result.pod.code})</span>
            </div>
            <div className="ml-auto text-xs text-slate-500">
              {result.card?.name ? `Source: ${result.card.name}` : ""}
            </div>
          </div>

          {/* Rate block */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Buy rate / CBM</div>
              <div className="text-xl font-bold text-slate-900">${result.rate.buy_rate_cbm}</div>
              <div className="text-xs text-slate-400 mt-1">Min: {fmt(result.rate.minimum_charge, "USD")}</div>
            </div>
            <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-3">
              <div className="text-xs text-emerald-700 font-medium">Sell rate / CBM</div>
              <div className="text-xl font-bold text-emerald-700">${result.rate.sell_rate_cbm}</div>
              <div className="text-xs text-emerald-600 mt-1">
                Markup: ${result.rate.effective_markup_usd}
                {result.rate.has_override && <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px]">OVERRIDE</span>}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Transit time</div>
              <div className="text-xl font-bold text-slate-900">{result.rate.tt_total_days ?? "—"} <span className="text-sm font-normal text-slate-500">days</span></div>
              <div className="text-xs text-slate-400 mt-1">
                {result.rate.tt_to_hub_days ?? "—"} + {result.rate.tt_hub_to_pod_days ?? "—"} ({result.rate.service})
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Competitors</div>
              <div className="text-xl font-bold text-slate-900">{Object.keys(result.competitors || {}).length}</div>
              <div className="text-xs text-slate-400 mt-1">
                {Object.entries(result.competitors || {}).map(([v, r]) =>
                  <span key={v} className="mr-2">{v}: ${r}</span>
                )}
                {!Object.keys(result.competitors || {}).length && "No competitor rates"}
              </div>
            </div>
          </div>

          {/* Export pricing builder ─────────────────────────────────
              Visible only on EXPORT searches. The final customer price is
              built as: FL buy (Singapore→destination) + Leg 1 addon (the
              Koper↔Singapore transhipment leg) + profit margin. Both
              addons are editable per quote. */}
          {direction === "export" && (() => {
            const flBuy = Number(result.rate?.buy_rate_cbm) || 0;
            const leg1  = Number(leg1Addon) || 0;
            const mgn   = Number(exportMargin) || 0;
            const finalSell = flBuy + leg1 + mgn;
            return (
              <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4" data-testid="export-pricing-builder">
                <div className="flex items-center gap-2 mb-3">
                  <Ship className="h-4 w-4 text-purple-700" />
                  <h4 className="text-sm font-semibold text-purple-900">Export pricing builder (Koper → {result.pol?.name})</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    <div className="text-xs text-slate-500">Leg 2 — FL buy / WM</div>
                    <div className="text-lg font-bold text-slate-900">${flBuy.toFixed(2)}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Singapore → {result.pol?.code}</div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-medium">Leg 1 addon ($/WM)</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={leg1Addon}
                      onChange={(e) => setLeg1Addon(e.target.value)}
                      placeholder="35"
                      data-testid="export-leg1-addon-input"
                      title="Koper ↔ Singapore transhipment leg. Default $35/WM, editable."
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-medium">Profit margin ($/WM)</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={exportMargin}
                      onChange={(e) => setExportMargin(e.target.value)}
                      placeholder="25"
                      data-testid="export-margin-input"
                      title="Profit margin on top of buy + Leg 1. Default $25/WM, editable."
                    />
                  </div>
                  <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3">
                    <div className="text-xs text-emerald-700 font-medium">Final sell / WM</div>
                    <div className="text-2xl font-bold text-emerald-700" data-testid="export-final-sell">
                      ${finalSell.toFixed(2)}
                    </div>
                    <div className="text-[10px] text-emerald-600 mt-0.5">
                      = {flBuy.toFixed(2)} + {leg1.toFixed(2)} + {mgn.toFixed(2)}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-3">
                  Final customer quote should be at <b>${finalSell.toFixed(2)}/WM</b>. Edit the two addons above
                  to flex pricing per opportunity.
                </p>
              </div>
            );
          })()}

          {/* Port surcharges */}
          {result.port_surcharges?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <div className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> Port-specific surcharges
              </div>
              <div className="flex flex-wrap gap-2">
                {result.port_surcharges.map((s, i) => (
                  <span key={i} className="px-2 py-1 bg-white border border-amber-200 rounded-full text-xs">
                    <span className="font-mono font-semibold">{s.code}</span> {s.amount} {s.currency || "USD"}
                    <span className="text-slate-400"> / {(s.basis || "per_shipment").replace("per_", "")}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Charges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ChargesTable title="Origin charges (POL)" rows={result.origin_charges} accent="slate" />
            <ChargesTable title="Destination charges (POD)" rows={result.destination_charges} accent="blue" />
          </div>

          {/* Agents */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                <Users className="h-4 w-4 text-blue-600" /> Origin agents (FLE — {result.pol.country || "?"})
              </div>
              {result.origin_agents?.length ? (
                <div className="space-y-2">{result.origin_agents.map((a, i) => <AgentCard key={i} a={a} />)}</div>
              ) : (
                <div className="text-xs text-slate-400 italic">No FLE agent found for this country.</div>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1">
                <Users className="h-4 w-4 text-emerald-600" /> Destination agents (FLE-D)
              </div>
              {result.destination_agents?.length ? (
                <div className="space-y-2">{result.destination_agents.map((a, i) => <AgentCard key={i} a={a} />)}</div>
              ) : (
                <div className="text-xs text-slate-400 italic">No FLE-D agent found.</div>
              )}
            </div>
          </div>

          {/* Bottom totals strip */}
          <div className="rounded-lg border border-slate-300 bg-slate-50 p-4 flex flex-wrap items-center gap-6">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div>
              <div className="text-xs text-slate-500">Ocean / CBM</div>
              <div className="font-bold text-slate-900">{fmt(result.totals_per_cbm?.ocean_usd)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Origin (sum)</div>
              <div className="font-bold text-slate-900">{fmt(result.totals_per_cbm?.origin_estimate)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Destination (sum)</div>
              <div className="font-bold text-slate-900">{fmt(result.totals_per_cbm?.destination_estimate, "EUR")}</div>
            </div>
            <div className="ml-auto text-xs text-slate-400 italic">
              Per-CBM estimate (excludes weight-based components).
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
